/**
 * EhrFounderNote - "Why we made it free.", the founder's note under the /ehr
 * hero. A free EHR first raises "what's the catch", so the page answers why
 * first. The reason is the mission, never a pricing model. No photo, by the founder's choice; the signature is the attribution.
 *
 * A figure with a blockquote and figcaption, so the note reads as Aouss's own
 * words, attributed to him. The card is ProblemCard's surface at the closing
 * stat card's radius, with a violet top bar from the hero's glows.
 */

import { acc } from "@/components/landing/home/home-accent";
import { useReveal } from "@/components/landing/landing-animations";
import { EhrHeading } from "./EhrHeading";

/* Literal page ink, like the waitlist hero. Long-form body at the About
   narrative's alpha; the caption at 0.66, the lightest ink that clears 4.5:1
   for small text on the cream card. */
const INK = "hsl(225 45% 14%)";
const BODY_COLOR = "hsl(225 45% 14% / 0.78)";
const CAPTION_COLOR = "hsl(225 45% 14% / 0.66)";
const RULE_COLOR = "hsl(225 45% 14% / 0.1)";
const CARD_BG = "hsl(38 30% 97%)";
const CARD_BORDER = "hsl(225 45% 14% / 0.08)";
const CARD_SHADOW = "0 30px 60px -44px hsl(225 45% 14% / 0.16)";

const NOTE = [
  "Payers are billion-dollar companies, and they wear independent practices down with paperwork, denials and delays. We believe AI billing is how practices fight back and stay independent.",
  "But so many practices that want AI in their billing feel trapped by an outdated EHR.",
  "So we built our own, and made it completely free: a truly modern EHR with AI billing built in, so you can run your practice and fight back against insurance companies.",
];

export function EhrFounderNote() {
  const noteRef = useReveal<HTMLElement>();

  return (
    <section
      data-screen-label="Why it's free"
      className="home-anim ehr-note-section"
      style={{
        position: "relative",
        padding: "140px 24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        color: INK,
        borderTop: "1px solid hsl(225 45% 14% / 0.06)",
      }}
    >
      <EhrHeading lead="Why we made it" beat="free." marginBottom={56} />

      <figure
        ref={noteRef}
        className="landing-reveal ehr-note-card"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 760,
          margin: 0,
          padding: "54px 60px 44px",
          background: CARD_BG,
          border: `1px solid ${CARD_BORDER}`,
          borderRadius: 28,
          boxShadow: CARD_SHADOW,
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(to right, ${acc("violet").ink}, transparent 72%)`,
          }}
        />

        <blockquote style={{ margin: 0 }}>
          {NOTE.map((paragraph, i) => (
            <p
              key={paragraph}
              style={{
                margin: i === NOTE.length - 1 ? 0 : "0 0 22px",
                fontSize: "clamp(17px,1.6vw,19px)",
                lineHeight: 1.7,
                color: BODY_COLOR,
                textWrap: "pretty",
              }}
            >
              {paragraph}
            </p>
          ))}
        </blockquote>

        <figcaption
          style={{
            marginTop: 34,
            paddingTop: 26,
            borderTop: `1px solid ${RULE_COLOR}`,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <span
            style={{
              fontFamily: "'Instrument Serif',Georgia,serif",
              fontStyle: "italic",
              fontSize: 28,
              lineHeight: 1.1,
              color: INK,
            }}
          >
            Aouss Sbai
          </span>
          <span
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: CAPTION_COLOR,
            }}
          >
            Founder &amp; CEO · Altair Health
          </span>
        </figcaption>
      </figure>

      <style>{`
        @media (max-width: 768px) {
          .ehr-note-section {
            padding-top: 88px !important;
            padding-bottom: 88px !important;
          }
          .ehr-note-card {
            padding: 36px 24px 30px !important;
            border-radius: 22px !important;
          }
        }
      `}</style>
    </section>
  );
}
